import { supabase } from "@/integrations/supabase/client";

/**
 * Painel da plataforma.
 *
 * Tudo aqui passa por função `SECURITY DEFINER` que confere o admin no banco.
 * A tela esconder o menu é conforto, não segurança: quem digitar a URL ou
 * chamar a API direto esbarra na mesma checagem.
 */

export interface ResumoPlataforma {
  empresas: number;
  empresasNovas30d: number;
  empresasAtivas7d: number;
  usuarios: number;
  assinantes: number;
  emTeste: number;
  inadimplentes: number;
  suspensas: number;
  receitaMensalCentavos: number;
  mensagens7d: number;
  conversasAbertas: number;
  contatos: number;
}

export interface EmpresaNaPlataforma {
  id: string;
  nome: string;
  criadoEm: string;
  suspensaEm: string | null;
  suspensaMotivo: string | null;
  membros: number;
  contatos: number;
  mensagens30d: number;
  ultimaAtividade: string | null;
  conexoesAtivas: number;
  statusAssinatura: string;
  assentos: number;
  trialFim: string | null;
  periodoFim: string | null;
  planoNome: string | null;
}

export interface MembroNaPlataforma {
  membroId: string;
  /** Conta de login. E por ela que a troca de senha acontece. */
  userId: string;
  nome: string | null;
  email: string | null;
  papel: string;
  ativo: boolean;
  desde: string;
}

export interface EntradaHistorico {
  acao: string;
  empresaNome: string | null;
  adminNome: string | null;
  detalhe: Record<string, unknown> | null;
  criadoEm: string;
}

export async function souAdminDaPlataforma(): Promise<boolean> {
  const { data, error } = await supabase.rpc("e_admin_plataforma");
  if (error) return false;
  return Boolean(data);
}

export async function resumoDaPlataforma(): Promise<ResumoPlataforma> {
  const { data, error } = await supabase.rpc("admin_resumo");
  if (error) throw error;

  const r = (data ?? [])[0] ?? {};
  return {
    empresas: r.empresas ?? 0,
    empresasNovas30d: r.empresas_novas_30d ?? 0,
    empresasAtivas7d: r.empresas_ativas_7d ?? 0,
    usuarios: r.usuarios ?? 0,
    assinantes: r.assinantes ?? 0,
    emTeste: r.em_teste ?? 0,
    inadimplentes: r.inadimplentes ?? 0,
    suspensas: r.suspensas ?? 0,
    receitaMensalCentavos: Number(r.receita_mensal_centavos ?? 0),
    mensagens7d: r.mensagens_7d ?? 0,
    conversasAbertas: r.conversas_abertas ?? 0,
    contatos: r.contatos ?? 0,
  };
}

export async function empresasDaPlataforma(busca?: string): Promise<EmpresaNaPlataforma[]> {
  const { data, error } = await supabase.rpc("admin_empresas", {
    p_busca: busca?.trim() || null,
  });
  if (error) throw error;

  return (data ?? []).map((e: Record<string, never>) => ({
    id: e.id,
    nome: e.nome,
    criadoEm: e.criado_em,
    suspensaEm: e.suspensa_em,
    suspensaMotivo: e.suspensa_motivo,
    membros: e.membros,
    contatos: e.contatos,
    mensagens30d: e.mensagens_30d,
    ultimaAtividade: e.ultima_atividade,
    conexoesAtivas: e.conexoes_ativas,
    statusAssinatura: e.status_assinatura,
    assentos: e.assentos,
    trialFim: e.trial_fim,
    periodoFim: e.periodo_fim,
    planoNome: e.plano_nome,
  }));
}

export async function membrosDaEmpresa(empresaId: string): Promise<MembroNaPlataforma[]> {
  const { data, error } = await supabase.rpc("admin_membros", { p_empresa_id: empresaId });
  if (error) throw error;

  return (data ?? []).map((m: Record<string, never>) => ({
    membroId: m.membro_id,
    userId: m.user_id,
    nome: m.nome,
    email: m.email,
    papel: m.papel,
    ativo: m.ativo,
    desde: m.desde,
  }));
}

/**
 * Troca a senha de um usuario, para o suporte de quem perdeu ate o e-mail.
 *
 * Passa por edge function porque so a service role fala com a API de admin do
 * GoTrue. Quem autoriza e registra e o banco, dentro dela.
 */
export async function trocarSenhaDoUsuario(userId: string, senha: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke("admin-usuario", {
    body: { acao: "senha", userId, senha },
  });
  if (error) {
    const resposta = (error as Error & { context?: unknown }).context;
    if (resposta instanceof Response) {
      try {
        const corpo = (await resposta.clone().json()) as { erro?: string };
        throw new Error(corpo?.erro ?? error.message);
      } catch (e) {
        if (e instanceof Error && e.message !== error.message) throw e;
      }
    }
    throw error;
  }
  const r = data as { erro?: string };
  if (r?.erro) throw new Error(r.erro);
}

export interface ResumoDaExclusao {
  empresaNome: string | null;
  contatos: number;
  conversas: number;
  mensagens: number;
  vendas: number;
  membros: number;
  usuariosApagados: number;
  conexoes: number;
  assinaturaAtiva: boolean;
}

async function chamarExclusao<T>(corpo: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("admin-apagar-empresa", { body: corpo });
  if (error) {
    // O motivo (codigo errado, nome que nao confere, expirado) vem no corpo:
    // o supabase-js troca tudo por "non-2xx" e e justamente essa frase que a
    // pessoa precisa ler para saber o que corrigir.
    const resposta = (error as Error & { context?: unknown }).context;
    if (resposta instanceof Response) {
      const corpoErro = await resposta
        .clone()
        .json()
        .catch(() => null);
      const detalhe = (corpoErro as { erro?: string } | null)?.erro;
      if (detalhe) throw new Error(detalhe);
    }
    throw error;
  }
  const r = data as T & { erro?: string };
  if (r?.erro) throw new Error(r.erro);
  return r;
}

/** Passo 1: manda o codigo para o e-mail do admin e diz o que vai sumir. */
export function pedirExclusao(empresaId: string) {
  return chamarExclusao<{ email: string; resumo: Record<string, never> }>({
    acao: "pedir",
    empresaId,
  });
}

/** Passo 2: as duas confirmacoes juntas. Nao tem desfazer depois disto. */
export function apagarEmpresa(params: {
  empresaId: string;
  codigo: string;
  nomeDigitado: string;
}) {
  return chamarExclusao<{ arquivos: number }>({ acao: "apagar", ...params });
}

export async function estenderTeste(empresaId: string, dias: number): Promise<void> {
  const { error } = await supabase.rpc("admin_estender_teste", {
    p_empresa_id: empresaId,
    p_dias: dias,
  });
  if (error) throw error;
}

export async function suspenderEmpresa(
  empresaId: string,
  suspender: boolean,
  motivo?: string,
): Promise<void> {
  const { error } = await supabase.rpc("admin_suspender_empresa", {
    p_empresa_id: empresaId,
    p_suspender: suspender,
    p_motivo: motivo ?? null,
  });
  if (error) throw error;
}

export async function definirMembroAtivo(membroId: string, ativo: boolean): Promise<void> {
  const { error } = await supabase.rpc("admin_definir_membro_ativo", {
    p_membro_id: membroId,
    p_ativo: ativo,
  });
  if (error) throw error;
}

export async function historicoDaPlataforma(): Promise<EntradaHistorico[]> {
  const { data, error } = await supabase.rpc("admin_historico", { p_limite: 30 });
  if (error) throw error;

  return (data ?? []).map((l: Record<string, never>) => ({
    acao: l.acao,
    empresaNome: l.empresa_nome,
    adminNome: l.admin_nome,
    detalhe: l.detalhe,
    criadoEm: l.criado_em,
  }));
}
